import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiResponse } from '@card-software/library';
import { environment } from '../../environments/environment';
import {
  WithholdingResidency,
  WithholdingTaxCode,
  WithholdingTaxCodeInsert,
  WithholdingTaxKind,
  WithholdingTaxType,
  WithholdingTaxTypeInsert,
} from '../models/withholding/withholding';

/** Country-keyed withholding-tax catalog (#4304): TYPE rules + effective-dated rate CODES. */
@Injectable({ providedIn: 'root' })
export class WithholdingTaxProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.financeApi + '/api/v1/withholding-taxes';

  // ---- Types ----

  public getTypes(countryCode?: string | null, includeInactive = true): Observable<ApiResponse<WithholdingTaxType[]>> {
    let params = new HttpParams().set('includeInactive', includeInactive);
    if (countryCode) params = params.set('countryCode', countryCode);
    return this.httpClient.get<ApiResponse<WithholdingTaxType[]>>(`${this.baseUrl}/types`, { params });
  }

  public getTypeById(id: string): Observable<ApiResponse<WithholdingTaxType>> {
    return this.httpClient.get<ApiResponse<WithholdingTaxType>>(`${this.baseUrl}/types/${id}`);
  }

  public createType(insert: WithholdingTaxTypeInsert): Observable<ApiResponse<WithholdingTaxType>> {
    return this.httpClient.post<ApiResponse<WithholdingTaxType>>(`${this.baseUrl}/types`, insert);
  }

  public updateType(id: string, update: WithholdingTaxTypeInsert): Observable<ApiResponse<WithholdingTaxType>> {
    return this.httpClient.put<ApiResponse<WithholdingTaxType>>(`${this.baseUrl}/types/${id}`, update);
  }

  // ---- Codes ----

  public getCodesByType(typeId: string): Observable<ApiResponse<WithholdingTaxCode[]>> {
    return this.httpClient.get<ApiResponse<WithholdingTaxCode[]>>(`${this.baseUrl}/types/${typeId}/codes`);
  }

  /** Codes usable for a provider's country/residency as of a date — the per-line picker source. */
  public getUsableCodes(
    countryCode: string,
    taxKind?: WithholdingTaxKind | null,
    residency?: WithholdingResidency | null,
    asOf?: string | null,
  ): Observable<ApiResponse<WithholdingTaxCode[]>> {
    let params = new HttpParams().set('countryCode', countryCode);
    if (taxKind != null) params = params.set('taxKind', taxKind);
    if (residency != null) params = params.set('residency', residency);
    if (asOf) params = params.set('asOf', asOf);
    return this.httpClient.get<ApiResponse<WithholdingTaxCode[]>>(`${this.baseUrl}/usable-codes`, { params });
  }

  public getCodeById(id: string): Observable<ApiResponse<WithholdingTaxCode>> {
    return this.httpClient.get<ApiResponse<WithholdingTaxCode>>(`${this.baseUrl}/codes/${id}`);
  }

  public createCode(insert: WithholdingTaxCodeInsert): Observable<ApiResponse<WithholdingTaxCode>> {
    return this.httpClient.post<ApiResponse<WithholdingTaxCode>>(`${this.baseUrl}/codes`, insert);
  }

  public updateCode(id: string, update: WithholdingTaxCodeInsert): Observable<ApiResponse<WithholdingTaxCode>> {
    return this.httpClient.put<ApiResponse<WithholdingTaxCode>>(`${this.baseUrl}/codes/${id}`, update);
  }
}
